import { Text, View, ScrollView, TouchableOpacity, RefreshControl, useWindowDimensions } from 'react-native';
import { ActivityIndicator } from 'react-native-paper';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { deletePath, savePath, savePathStep, savePathRecurrence, enrollPerson, enrollRole, manuallyAdvanceStep, skipDelayStep } from '@/data/paths';
import { publishAutomation, validateAutomationBindings, startAutomationRun, saveAutomationTrigger } from '@/data/automations';
import { buildPublishPayload, isAutomationConflict, extractAutomationErrors, triggerLabel, isStepParkedForReview, describeStepWaitState, APPROVAL_BRANCHES } from '@/utils/automationBuilder';
import TriggerConfigSheet from '@/components/Paths/v2/TriggerConfigSheet';
import PublishResultSheet from '@/components/Paths/v2/PublishResultSheet';
import StartRunSheet from '@/components/Paths/v2/StartRunSheet';
import { supabase } from '@/data/client';
import { useLocalSearchParams, useRouter, useNavigation } from 'expo-router';
import usePlatform from '@/utils/hooks/usePlatform';
import { showAlert } from '@/utils/showAlert';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { StyleSheet } from 'react-native-unistyles';
import Unicon from '@/components/General/Unicon';
import Avatar from '@/components/People/Avatar';
import PathFlowTimeline from '@/components/Paths/PathFlowTimeline';
import useSaveShortcut from '@/utils/hooks/useSaveShortcut';
import PathFlowEditor from '@/components/Paths/PathFlowEditor';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import { saveToRecents } from '@/utils/recentlyVisited';
import SolidButton from '@/components/General/SolidButton';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SelectInput from '@/components/General/inputs/SelectInput';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import RecurrenceSettingsNew from '@/components/Events/RecurrenceSettingsNew';
import SheetModal from '@/components/General/SheetModal';
import Toast from 'react-native-toast-message';
import { PREDEFINED_TASK_TYPES } from '@/components/Paths/flow/constants';
import { AUTOMATION_DUE_TYPES } from '@/utils/constants/taskTypes';
import { computePathProgress } from '@/components/Paths/pathProgress';

const TRIGGER_LABELS = {
    MANUAL: 'Manual Run',
    PATH_ACTION: 'Automation Action',
    PERSON_CREATED: 'Person Created',
    FORM_ENTRY: 'Form Entry',
    TRAINING_COMPLETED: 'Training Module Completed',
    SMALLGROUP_COMPLETED: 'Small Group Completed (Member)',
    SCHEDULE: 'Scheduled',
    ATTENDANCE: 'Attendance Trend',
    EVENT_CHECK_IN: 'Event Check In',
};

const STEP_TYPE_LABELS = {
    ACTION: 'Action',
    CONDITION: 'Condition',
    DELAY: 'Delay',
    TASK: 'Task',
    TRIGGER: 'Trigger',
};

const ACTION_TYPE_LABELS = {
    SEND_EMAIL: 'Send Email',
    SEND_SMS: 'Send SMS',
    ENROLL_TRAINING: 'Enroll in Training',
    ADD_TO_SMALL_GROUP: 'Add to Small Group',
    POST_TO_BOARD: 'Post to Board',
    ADD_TO_ROLE: 'Add to Role',
    INVITE_TO_REACH: 'Invite to Reach',
    TRIGGER_PATH: 'Trigger Automation',
    UPDATE_PERSON: 'Update Person',
    REGISTER_FOR_EVENT: 'Register for Event',
    CHECK_INTO_EVENT: 'Check Into Event',
    RESOLVE_PERSON: 'Resolve Person',
    UPSERT_PERSON: 'Create or Update Person',
    CREATE_TASK: 'Create Task',
    WAIT_FOR_TASK: 'Wait for Task',
    START_FOLLOW_UP_PATH: 'Start Follow Up Path',
    INVOKE_AUTOMATION: 'Invoke Automation',
    REQUEST_APPROVAL: 'Request Approval',
};

const STATUS_CONFIG = {
    ACTIVE: { label: 'Active', colorVariant: 'primary' },
    COMPLETED: { label: 'Completed', colorVariant: 'success' },
    CANCELLED: { label: 'Cancelled', colorVariant: 'error' },
    PAUSED: { label: 'Paused', colorVariant: 'warning' },
};

const TAB_ROUTES = [
    { key: 'flow', title: 'Flow Builder' },
    { key: 'enrollments', title: 'Runs' },
];

const EnrollmentCard = ({ enrollment, router, steps, edges, onContinue, onSkipDelay, onPress }) => {
    const person = enrollment.Person; // null on personless v2 runs (§14.1)
    const personName = person
        ? `${person.firstName || ''} ${person.lastName || ''}`.trim()
        : 'Run (no person)';
    const enrollmentSteps = enrollment.EnrollmentSteps || [];
    const waitingManualStep = enrollmentSteps.find(s => s.status === 'WAITING_MANUAL');
    const waitingDelayStep = enrollmentSteps.find(s => s.status === 'WAITING');
    const inProgressStep = enrollmentSteps.find(s => s.status === 'IN_PROGRESS');
    const failedStep = enrollmentSteps.find(s => s.status === 'FAILED');
    // An ACTIVE enrollment with a FAILED step is effectively stopped — badge it
    // as Failed so it stands out in the list.
    // Parked-for-review steps (v2): badge the run so it stands out.
    const parkedStep = enrollmentSteps.find(s => isStepParkedForReview(s));
    const status = parkedStep && enrollment.status === 'ACTIVE'
        ? { label: 'Needs Review', colorVariant: 'warning' }
        : failedStep && enrollment.status === 'ACTIVE'
        ? { label: 'Failed', colorVariant: 'error' }
        : STATUS_CONFIG[enrollment.status] || STATUS_CONFIG.ACTIVE;

    // Current step = first step that's not done (COMPLETED/SKIPPED)
    const currentStep = waitingManualStep || inProgressStep || waitingDelayStep ||
        enrollmentSteps.find(s => s.status === 'PENDING' || s.status === 'FAILED');
    const currentStepName = currentStep?.PathStep?.name;
    // A WAITING step is the delay's target — surface the delay window instead
    // of the next step's name.
    const delayedUntil = waitingDelayStep?.scheduledFor ? new Date(waitingDelayStep.scheduledFor) : null;
    const delayedUntilLabel = delayedUntil
        ? delayedUntil.toLocaleString('default', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
        : null;

    const { completedCount, longestPathTotal: totalCount, progressPercent } = computePathProgress({
        steps: steps || [],
        edges: edges || [],
        enrollmentSteps,
    });

    return (
        <TouchableOpacity
            style={styles.enrollmentCard}
            activeOpacity={0.7}
            onPress={() => {
                if (onPress) onPress(enrollment);
                else router.push("/people/" + person?.id);
            }}
        >
            <Avatar size={36} profile={person} disableBorder />
            <View style={styles.enrollmentInfo}>
                <Text style={styles.enrollmentName}>{personName}</Text>
                {enrollment.status === 'ACTIVE' && parkedStep ? (
                    <Text style={styles.enrollmentFailedStep} numberOfLines={1}>Paused: {parkedStep.PathStep?.name || 'step needs review'}</Text>
                ) : enrollment.status === 'ACTIVE' && failedStep ? (
                    <Text style={styles.enrollmentFailedStep} numberOfLines={1}>Failed: {failedStep.PathStep?.name || 'Unknown step'}</Text>
                ) : enrollment.status === 'ACTIVE' && delayedUntilLabel ? (
                    <Text style={styles.enrollmentCurrentStep} numberOfLines={1}>Delayed until {delayedUntilLabel}</Text>
                ) : currentStepName && enrollment.status === 'ACTIVE' ? (
                    <Text style={styles.enrollmentCurrentStep} numberOfLines={1}>On: {currentStepName}</Text>
                ) : null}
                {/* Progress bar */}
                <View style={styles.progressBarContainer}>
                    <View style={styles.progressBarFill(progressPercent)} />
                </View>
                <Text style={styles.enrollmentMeta}>{completedCount} / {totalCount} steps</Text>
            </View>
            <View style={styles.statusBadge(status.colorVariant)}>
                <Text style={styles.statusBadgeText(status.colorVariant)}>{status.label}</Text>
            </View>
            {waitingManualStep && (
                <TouchableOpacity
                    style={styles.continueBtn}
                    onPress={(e) => { e.stopPropagation?.(); onContinue?.(waitingManualStep); }}
                >
                    <Text style={styles.continueBtnText}>Continue</Text>
                </TouchableOpacity>
            )}
            {waitingDelayStep && (
                <TouchableOpacity
                    style={styles.skipDelayBtn}
                    onPress={(e) => { e.stopPropagation?.(); onSkipDelay?.(waitingDelayStep); }}
                >
                    <Text style={styles.skipDelayBtnText}>Continue Now</Text>
                </TouchableOpacity>
            )}
        </TouchableOpacity>
    );
};

export default function PathDetailScreen() {
    const { pathID } = useLocalSearchParams();
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedStep, setSelectedStep] = useState(null);

    const router = useRouter();
    const navigation = useNavigation();
    const queryClient = useQueryClient();
    const { isWeb, isNative } = usePlatform();
    const layout = useWindowDimensions();
    const isWideScreen = layout.width >= 900;

    const { data: loggedInUser } = Query.people.loggedInUser();
    const saveShortcut = useSaveShortcut(loggedInUser?.id);
    const { data: path, isFetching } = Query.paths.byId({
        id: pathID,
        options: {
            refetchInterval: (query) => {
                const enrollments = query.state.data?.Enrollments || [];
                return enrollments.some(e => e.status === 'ACTIVE') ? 10000 : false;
            },
        },
    });

    // Task lookups for the web Step Details panel (TASK steps): task name
    // resolution + per-path assignment pool for next-assignee prediction.
    const pathsTaskQuery = Query.tasks.byDueType({ dueType: AUTOMATION_DUE_TYPES, options: { staleTime: Infinity } });
    const pathsTasks = pathsTaskQuery?.data || [];
    const { data: pathAssignmentMembers } = useQuery({
        queryKey: ['pathStepAssignmentMembers', pathID],
        queryFn: async () => {
            const { data, error } = await supabase.from('TaskAssignmentMembers')
                .select('*, Person:People(id, firstName, lastName)')
                .eq('pathID', pathID)
                .order('position', { ascending: true });
            if (error) return [];
            return data || [];
        },
        enabled: !!pathID,
        staleTime: 1000 * 60 * 5,
    });

    // Track recently visited
    useEffect(() => {
        if (path?.id) {
            saveToRecents({
                id: path.id,
                url: `/automations/${path.id}`,
                title: path.name,
                params: { pathID: path.id },
            });
        }
    }, [path?.id]);

    const steps = useMemo(() => path?.Steps || [], [path]);
    const edges = useMemo(() => path?.Edges || [], [path]);
    const nonTriggerSteps = useMemo(() => steps.filter(s => s.stepType !== 'TRIGGER'), [steps]);
    const triggerStep = useMemo(() => steps.find(s => s.stepType === 'TRIGGER'), [steps]);
    const triggerType = triggerStep?.config?.triggerType;
    const isV2 = path?.schemaVersion === 2;

    // ── v2 builder state (§14.4/§15.4) ──
    const [triggerSheetOpen, setTriggerSheetOpen] = useState(false);
    const [publishSheetState, setPublishSheetState] = useState(null); // {result} | {errors, title}
    const [startRunSheetOpen, setStartRunSheetOpen] = useState(false);
    const { data: triggerCatalog } = Query.automations.triggerCatalog({ options: { enabled: isV2 } });
    const { data: v2Registry } = Query.automations.variables({
        pathID,
        options: { enabled: isV2 && !!pathID, staleTime: 1000 * 60 * 5 },
    });
    const triggerCatalogRow = useMemo(
        () => (triggerCatalog || []).find(r => r.triggerType === triggerType) || null,
        [triggerCatalog, triggerType]);
    const enrollments = useMemo(() => path?.Enrollments || [], [path]);
    const activeEnrollments = useMemo(() => enrollments.filter(e => e.status === 'ACTIVE'), [enrollments]);
    const pastEnrollments = useMemo(() => enrollments.filter(e => e.status !== 'ACTIVE'), [enrollments]);
    // Parked review steps across all runs — publish conflicts stay visible
    // until resolved (§15.4 step 8).
    const parkedReviewItems = useMemo(() => {
        if (!isV2) return [];
        const items = [];
        for (const en of enrollments) {
            for (const es of (en.EnrollmentSteps || [])) {
                if (!isStepParkedForReview(es)) continue;
                const wait = describeStepWaitState(es, { steps });
                const person = en.Person;
                items.push({
                    enrollmentStepID: es.id,
                    enrollmentID: en.id,
                    title: wait?.title || es.PathStep?.name || 'Step needs review',
                    personName: person ? `${person.firstName || ''} ${person.lastName || ''}`.trim() : 'No person',
                });
            }
        }
        return items;
    }, [isV2, enrollments, steps]);
    const { data: triggeringForms } = Query.forms.triggeringPath({
        pathID,
        options: { enabled: !!pathID && triggerType === 'FORM_ENTRY' },
    });
    const { data: triggeringEvents } = Query.paths.eventsForCheckInPath({
        pathID,
        options: { enabled: !!pathID && triggerType === 'EVENT_CHECK_IN' },
    });
    const nextStepPosition = useMemo(() =>
        nonTriggerSteps.length > 0 ? Math.max(...nonTriggerSteps.map(s => s.position)) + 1 : 1,
    [nonTriggerSteps]);

    const deletePathMutation = useMutation({
        mutationFn: () => deletePath({ pathID }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
            router.back();
        },
    });

    const savePathStepMutation = useMutation({
        mutationFn: savePathStep,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
        },
    });

    const togglePathStatusMutation = useMutation({
        mutationFn: (isActive) => savePath({ id: pathID, name: path.name, isActive }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
        },
    });

    const advanceStepMutation = useMutation({
        mutationFn: ({ enrollmentStepID }) => manuallyAdvanceStep({ enrollmentStepID, completedBy: loggedInUser?.id }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const skipDelayMutation = useMutation({
        mutationFn: ({ enrollmentStepID }) => skipDelayStep({ enrollmentStepID, completedBy: loggedInUser?.id }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
        },
    });

    const enrollPersonMutation = useMutation({
        mutationFn: (person) => {
            if (isV2) {
                return startAutomationRun({ pathID, input: { personID: person.id, input: {} } });
            }
            return enrollPerson({ pathID, personID: person.id, enrolledBy: loggedInUser?.id });
        },
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) }),
    });

    // Personless run start for v2 triggers that allow it (§14.1).
    const startRunMutation = useMutation({
        mutationFn: () => startAutomationRun({ pathID, input: { input: {} } }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) }),
    });

    // §15.4 publish: validate → expectedRevision-guarded publish → honest
    // impact/reconcile summary. Conflicts offer a reload.
    const publishMutation = useMutation({
        mutationFn: async () => {
            const check = await validateAutomationBindings({ pathID });
            if (!check?.valid) {
                return { validationFailed: true, errors: check?.errors || [], warnings: check?.warnings || [] };
            }
            return publishAutomation({
                pathID,
                payload: buildPublishPayload({ path, isActive: path?.isActive }),
            });
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.all() });
            queryClient.invalidateQueries({ queryKey: QueryKeys.automations.variables(pathID) });
            if (data?.validationFailed) {
                setPublishSheetState({ errors: data.errors, warnings: data.warnings, title: 'Fix these before publishing' });
            } else {
                setPublishSheetState({ result: data });
            }
        },
        onError: (error) => {
            const errors = extractAutomationErrors(error);
            if (errors.length) {
                setPublishSheetState({ errors, title: 'Fix these before publishing' });
            } else if (isAutomationConflict(error)) {
                setPublishSheetState({ errors: [{ message: error.message || 'This automation changed since you opened it.' }], title: 'Publish conflict', showReload: true });
            }
        },
    });

    // Trigger save → then validate bindings so a trigger/source change reports
    // exactly which step bindings it invalidated (P9 exit gate).
    const saveTriggerMutation = useMutation({
        mutationFn: async ({ triggerType: t, triggerConfig }) => {
            await saveAutomationTrigger({
                stepID: triggerStep?.id,
                pathID,
                position: triggerStep?.position ?? 0,
                name: triggerStep?.name,
                triggerType: t,
                triggerConfig,
            });
            return validateAutomationBindings({ pathID }).catch(() => null);
        },
        onSuccess: (check) => {
            setTriggerSheetOpen(false);
            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.automations.variables(pathID) });
            if (check && check.valid === false) {
                setPublishSheetState({ errors: check.errors || [], warnings: check.warnings || [], title: 'Trigger change invalidated bindings' });
            } else {
                Toast.show({ type: 'success', text2: 'Trigger updated!' });
            }
        },
    });

    const enrollRoleMutation = useMutation({
        mutationFn: (role) => enrollRole({ pathID, roleID: role.id, enrolledBy: loggedInUser?.id }),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) }),
    });

    const enrolling = enrollPersonMutation.isPending || enrollRoleMutation.isPending || startRunMutation.isPending;
    // v2 MANUAL and PATH_ACTION defs both start through the declared-input
    // form (StartRunSheet); v1 keeps the legacy person/role pickers.
    const canEnroll = (loggedInUser?.can?.("Enroll People in Paths") || loggedInUser?.isAdmin)
        && (isV2
            ? ['MANUAL', 'PATH_ACTION'].includes(triggerStep?.config?.triggerType)
            : triggerStep?.config?.triggerType === 'MANUAL');
    const canPublish = isV2 && (loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin);
    const handleEnrollPerson = useCallback((person) => person?.id && enrollPersonMutation.mutate(person), [enrollPersonMutation]);
    const handleEnrollRole = useCallback((role) => role?.id && enrollRoleMutation.mutate(role), [enrollRoleMutation]);

    const refresh = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
    }, [queryClient, pathID]);

    const handleUpdateTrigger = useCallback(async (triggerType, triggerConfig) => {
        // v2: trigger changes go through the config sheet — source config and
        // post-change binding validation happen there.
        if (isV2) {
            setTriggerSheetOpen(true);
            return;
        }
        if (!triggerStep) return;
        await savePathStepMutation.mutateAsync({
            id: triggerStep.id,
            pathID,
            position: triggerStep.position,
            name: triggerStep.name,
            stepType: 'TRIGGER',
            advanceOn: 'AUTOMATIC',
            config: { ...(triggerStep.config || {}), triggerType, triggerConfig: triggerConfig || {} },
        });

        if (triggerType === 'SCHEDULE' && triggerConfig) {
            const { roleID, defaultStartTime, rruleString, recurrenceEndDate } = triggerConfig;
            if (roleID && defaultStartTime && rruleString) {
                await savePathRecurrence({
                    pathID,
                    roleID,
                    firstOccurrenceAt: defaultStartTime,
                    rruleString,
                    recurrenceEndDate,
                });
            }
        }

        refresh();
    }, [pathID, triggerStep, savePathStepMutation, refresh]);

    const pathRecurrence = path?.Recurrence?.[0] || null;
    const [recurrence, setRecurrence] = useState({
        isRecurring: true,
        rruleString: pathRecurrence?.rruleString || null,
        recurrenceEndDate: pathRecurrence?.recurrenceEndDate || null,
        defaultStartTime: pathRecurrence?.firstOccurrenceAt || null,
    });
    const [selectedRole, setSelectedRole] = useState(pathRecurrence?.Role || null);
    const [recurrenceModalOpen, setRecurrenceModalOpen] = useState(false);

    useEffect(() => {
        setRecurrence({
            isRecurring: true,
            rruleString: pathRecurrence?.rruleString || null,
            recurrenceEndDate: pathRecurrence?.recurrenceEndDate || null,
            defaultStartTime: pathRecurrence?.firstOccurrenceAt || null,
        });
        setSelectedRole(pathRecurrence?.Role || null);
    }, [pathRecurrence?.id]);

    const handleSaveSchedule = useCallback(() => {
        if (!selectedRole?.id || !recurrence.defaultStartTime || !recurrence.rruleString) {
            Toast.show({ type: 'error', text2: 'Select a role, start time, and recurrence pattern.' });
            return;
        }
        handleUpdateTrigger('SCHEDULE', {
            roleID: selectedRole?.id,
            defaultStartTime: recurrence.defaultStartTime,
            rruleString: recurrence.rruleString,
            recurrenceEndDate: recurrence.recurrenceEndDate,
        });
        setRecurrenceModalOpen(false);
    }, [selectedRole, recurrence, handleUpdateTrigger]);

    const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
    const [attendanceMode, setAttendanceMode] = useState(triggerStep?.config?.triggerConfig?.mode || 'MISSED');
    const [attendanceWeeks, setAttendanceWeeks] = useState(String(triggerStep?.config?.triggerConfig?.weeks || ''));
    const [attendanceRole, setAttendanceRole] = useState(triggerStep?.config?.triggerConfig?.roleID ? { id: triggerStep.config.triggerConfig.roleID } : null);

    useEffect(() => {
        setAttendanceMode(triggerStep?.config?.triggerConfig?.mode || 'MISSED');
        setAttendanceWeeks(String(triggerStep?.config?.triggerConfig?.weeks || ''));
        setAttendanceRole(triggerStep?.config?.triggerConfig?.roleID ? { id: triggerStep.config.triggerConfig.roleID } : null);
    }, [triggerStep?.id, triggerStep?.config?.triggerConfig]);

    const handleSaveAttendance = useCallback(async () => {
        const weeks = parseInt(attendanceWeeks) || 1;
        await handleUpdateTrigger('ATTENDANCE', {
            mode: attendanceMode,
            weeks,
            roleID: attendanceRole?.id || null,
        });
        setAttendanceModalOpen(false);
    }, [attendanceMode, attendanceWeeks, attendanceRole, handleUpdateTrigger]);

    const handleEditStep = useCallback((step) => {
        router.push({ pathname: "/automations/steps/create", params: { id: step.id, pathID } });
    }, [router, pathID]);

    const handleAddStep = useCallback((sourceStepID, branchID, branchLabel, canvasPosition) => {
        // If no source specified and this is the first step, auto-connect to trigger
        const autoSource = sourceStepID || (nonTriggerSteps.length === 0 ? triggerStep?.id : undefined);
        router.push({
            pathname: "/automations/steps/create",
            params: {
                pathID, nextPosition: nextStepPosition, sourceStepID: autoSource || undefined,
                branchID: branchID || undefined, branchLabel: branchLabel || undefined,
                canvasPosition: canvasPosition ? JSON.stringify(canvasPosition) : undefined,
            },
        });
    }, [router, pathID, nextStepPosition, nonTriggerSteps, triggerStep]);

    const handleInsertStep = useCallback(({ edgeID, canvasPosition }) => {
        router.push({
            pathname: "/automations/steps/create",
            params: {
                pathID,
                nextPosition: nextStepPosition,
                insertOnEdgeID: edgeID,
                canvasPosition: canvasPosition ? JSON.stringify(canvasPosition) : undefined,
            },
        });
    }, [router, pathID, nextStepPosition]);

    const handleDuplicateStep = useCallback((step, sourceStepID, canvasPosition) => {
        router.push({
            pathname: "/automations/steps/create",
            params: {
                pathID,
                nextPosition: nextStepPosition,
                sourceStepID: sourceStepID || undefined,
                duplicateFromID: step.id,
                canvasPosition: canvasPosition ? JSON.stringify(canvasPosition) : undefined,
            },
        });
    }, [router, pathID, nextStepPosition]);

    useEffect(() => {
        const canEdit = loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin;
        const canDelete = loggedInUser?.can?.("Delete Paths") || loggedInUser?.isAdmin;
        const options = [];

        if (path?.id) {
            options.push({
                name: "Save to Shortcuts",
                icon: dropdownIcons.favorite,
                onSelect: () => saveShortcut({ screen: `/automations/${path.id}`, label: path?.name || 'Automation', icon: 'route-outline' })
            });
        }

        if (canEdit) {
            options.push({
                name: "Edit Automation",
                icon: dropdownIcons.edit,
                onSelect: () => router.push({ pathname: "/automations/create", params: { id: pathID } }),
            });
        }
        if (canDelete) {
            options.push({
                name: "Delete Automation",
                icon: dropdownIcons.delete,
                destructive: true,
                onSelect: () => {
                    showAlert({
                        title: "Delete Automation",
                        subtitle: `Are you sure you want to delete "${path?.name}"? This cannot be undone.`,
                        isDestructive: true,
                        confirmText: "Delete",
                        onConfirm: () => deletePathMutation.mutate(),
                    });
                },
            });
        }

        navigation.setOptions({
            headerRight: () => options.length > 0 ? <DropdownMenu options={options} /> : null,
        });
    }, [navigation, loggedInUser, path, pathID, saveShortcut]);

    // ── Info row helper (matches people/event detail style) ──────────────────────
    const InfoRow = useCallback(({ icon, label, value, onPress, children, isEmpty }) => {
        if (!value && !children && !isEmpty) return null;
        const content = (
            <View style={styles.infoRow}>
                <View style={styles.infoRowLeft}>
                    <Unicon name={icon} size={18} colorVariant="textSecondary" />
                    <Text style={styles.infoRowLabel}>{label}</Text>
                </View>
                <View style={styles.infoRowRight}>
                    {children || <Text style={[styles.infoRowValue, onPress && styles.linkText, isEmpty && styles.infoEmptyText]}>{value}</Text>}
                </View>
            </View>
        );
        return onPress ? <TouchableOpacity onPress={onPress}>{content}</TouchableOpacity> : content;
    }, []);

    // ── Left info panel (matches people/event detail style) ───────────────────────
    const renderInfoPanel = () => (
        <View style={styles.leftPanel}>
            <ScrollView
                contentContainerStyle={styles.leftPanelContent}
                refreshControl={<RefreshControl refreshing={isFetching} onRefresh={refresh} />}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
            >
                {/* Header Card */}
                <View style={styles.headerCard}>
                    <View style={styles.headerTitleRow}>
                        <View style={styles.headerIconBadge}>
                            <Unicon name="git-network-outline" size={24} colorVariant="paths" />
                        </View>
                        <View style={styles.headerTitleInfo}>
                            <Text style={styles.headerTitle}>{path.name}</Text>
                            {!!path.description && (
                                <Text style={styles.headerSubtitle}>{path.description}</Text>
                            )}
                        </View>
                    </View>

                    {/* Stats */}
                    <View style={styles.statsRow}>
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Steps</Text>
                            <Text style={styles.statValue}>{nonTriggerSteps.length}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Active</Text>
                            <Text style={styles.statValue}>{activeEnrollments.length}</Text>
                        </View>
                        <View style={styles.statDivider} />
                        <View style={styles.statItem}>
                            <Text style={styles.statLabel}>Total</Text>
                            <Text style={styles.statValue}>{enrollments.length}</Text>
                        </View>
                    </View>

                    {/* Actions */}
                    {((loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) || canEnroll) && (
                        <View style={styles.actionRow}>
                            {canEnroll && !isV2 && (
                                <View style={styles.enrollButtonRow}>
                                    <View style={styles.enrollButtonCell}>
                                        <SheetSelector
                                            type="people"
                                            title="Run Person"
                                            placeholder="Select a person"
                                            onChange={handleEnrollPerson}
                                            value={null}
                                            disabled={enrolling}
                                        >
                                            <SolidButton
                                                variant="muted"
                                                type="primary"
                                                text="Start Run"
                                                iconName="person-add-outline"
                                                disabled={enrolling}
                                                testID="path-enroll-person"
                                                width="100%"
                                            />
                                        </SheetSelector>
                                    </View>
                                    <View style={styles.enrollButtonCell}>
                                        <SheetSelector
                                            type="roles"
                                            title="Run Role"
                                            placeholder="Select a role"
                                            onChange={handleEnrollRole}
                                            value={null}
                                            disabled={enrolling}
                                        >
                                            <SolidButton
                                                variant="muted"
                                                type="primary"
                                                text="Start Role Run"
                                                iconName="people-outline"
                                                disabled={enrolling}
                                                testID="path-enroll-role"
                                                width="100%"
                                            />
                                        </SheetSelector>
                                    </View>
                                </View>
                            )}
                            {canEnroll && isV2 && (
                                <View style={styles.enrollButtonCell}>
                                    <SolidButton
                                        variant="muted"
                                        type="primary"
                                        text="Start Run"
                                        iconName="play-outline"
                                        onPress={() => setStartRunSheetOpen(true)}
                                        disabled={enrolling}
                                        testID="automation-start-run"
                                        width="100%"
                                    />
                                </View>
                            )}
                            {(loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) && (
                                <View style={styles.enrollButtonCell}>
                                    <SolidButton
                                        variant="muted"
                                        type="primary"
                                        text="Edit Automation"
                                        iconName="create"
                                        onPress={() => router.push({ pathname: "/automations/create", params: { id: pathID } })}
                                        width="100%"
                                    />
                                </View>
                            )}
                            {canPublish && (
                                <View style={styles.enrollButtonCell}>
                                    <SolidButton
                                        type="primary"
                                        text="Publish"
                                        iconName="cloud-upload-outline"
                                        onPress={() => publishMutation.mutate()}
                                        disabled={publishMutation.isPending}
                                        loading={publishMutation.isPending}
                                        testID="automation-publish"
                                        width="100%"
                                    />
                                </View>
                            )}
                            {canPublish && (
                                <View style={styles.enrollButtonCell}>
                                    <SolidButton
                                        variant="muted"
                                        type="primary"
                                        text="Edit Trigger"
                                        iconName="flash-outline"
                                        onPress={() => setTriggerSheetOpen(true)}
                                        testID="automation-edit-trigger"
                                        width="100%"
                                    />
                                </View>
                            )}
                            {triggerType === 'SCHEDULE' && (loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) && (
                                <SolidButton
                                    variant="muted"
                                    type="primary"
                                    text="Manage Schedule"
                                    iconName="calendar-outline"
                                    onPress={() => setRecurrenceModalOpen(true)}
                                    testID="path-manage-schedule"
                                />
                            )}
                            {triggerType === 'ATTENDANCE' && (loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) && (
                                <SolidButton
                                    variant="muted"
                                    type="primary"
                                    text="Manage Attendance Trend"
                                    iconName="people-outline"
                                    onPress={() => setAttendanceModalOpen(true)}
                                    testID="path-manage-attendance"
                                />
                            )}
                        </View>
                    )}
                </View>

                {/* Details */}
                <View style={styles.infoSection}>
                    <Text style={styles.sectionLabel}>Details</Text>

                    <InfoRow icon="pulse-outline" label="Status">
                        <DropdownMenu
                            options={[
                                {
                                    name: path.isActive ? 'Deactivate' : 'Activate',
                                    icon: path.isActive ? dropdownIcons.close : dropdownIcons.check,
                                    onSelect: () => togglePathStatusMutation.mutate(!path.isActive),
                                },
                            ]}
                            triggerComponent={
                                <View style={styles.statusChip(path.isActive ? 'success' : 'textSecondary')}>
                                    <View style={styles.statusDot(path.isActive ? 'success' : 'textSecondary')} />
                                    <Text style={styles.statusChipText(path.isActive ? 'success' : 'textSecondary')}>
                                        {path.isActive ? 'Active' : 'Inactive'}
                                    </Text>
                                </View>
                            }
                        />
                    </InfoRow>

                    <InfoRow
                        icon="flash-outline"
                        label="Trigger"
                        value={isV2
                            ? triggerLabel(triggerStep?.config?.triggerType)
                            : (TRIGGER_LABELS[triggerStep?.config?.triggerType] || triggerStep?.config?.triggerType || 'Manual')}
                    />
                    {isV2 && (
                        <InfoRow
                            icon="git-commit-outline"
                            label="Revision"
                            value={`r${path?.revision ?? 1}`}
                        />
                    )}

                    {path.CreatedBy && (
                        <InfoRow icon="person-outline" label="Created By">
                            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                <Avatar size={20} profile={path.CreatedBy} disableBorder />
                                <Text style={styles.infoRowValue}>
                                    {path.CreatedBy.firstName} {path.CreatedBy.lastName}
                                </Text>
                            </View>
                        </InfoRow>
                    )}
                </View>

                {/* Step Details (web only — dynamically shows selected step from canvas) */}
                {isWeb && selectedStep && (
                    <View style={styles.infoSection}>
                        <View style={styles.stepDetailsHeader}>
                            <Text style={styles.sectionLabel}>Step Details</Text>
                            {(loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) && (
                                <TouchableOpacity
                                    style={styles.editStepBtn}
                                    onPress={() => handleEditStep(selectedStep)}
                                >
                                    <Unicon name="create-outline" size={14} colorVariant="primary" />
                                    <Text style={styles.editStepBtnText}>Edit</Text>
                                </TouchableOpacity>
                            )}
                        </View>

                        <InfoRow icon="pricetag-outline" label="Name" value={selectedStep.name} />
                        <InfoRow icon="cube-outline" label="Type" value={STEP_TYPE_LABELS[selectedStep.stepType] || selectedStep.stepType} />
                        {selectedStep.description && (
                            <InfoRow icon="document-text-outline" label="Description" value={selectedStep.description} />
                        )}
                        {selectedStep.stepType === 'ACTION' && selectedStep.actionType && (
                            <InfoRow icon="flash-outline" label="Action" value={ACTION_TYPE_LABELS[selectedStep.actionType] || selectedStep.actionType} />
                        )}
                        {selectedStep.stepType === 'DELAY' && selectedStep.config?.duration && (
                            <InfoRow icon="time-outline" label="Wait" value={`${selectedStep.config.duration} ${selectedStep.config.unit || 'days'}`} />
                        )}
                        {selectedStep.stepType === 'TASK' && (() => {
                            const config = selectedStep.config || {};
                            const strategy = config.assignmentStrategy || 'ROUND_ROBIN';
                            const strategyLabel = strategy === 'ORDERED' ? 'Ordered' : strategy === 'ASSIGN_ENROLLEE' ? 'Assign Enrollee' : 'Round Robin';
                            if (config.taskSource === 'PREDEFINED') {
                                const typeDef = PREDEFINED_TASK_TYPES[config.predefinedType];
                                return (
                                    <>
                                        <InfoRow icon="pricetag-outline" label="Type" value={typeDef?.label || config.predefinedType || 'Predefined'} />
                                        {config.predefinedType === 'PERSONAL_SMS' && config.message && (
                                            <InfoRow icon="chatbubble-outline" label="Message"><Text style={styles.infoRowValue}>{config.message}</Text></InfoRow>
                                        )}
                                        {config.predefinedType === 'PERSONAL_EMAIL' && (
                                            <>
                                                {config.subject && <InfoRow icon="mail-outline" label="Subject"><Text style={styles.infoRowValue}>{config.subject}</Text></InfoRow>}
                                                {config.body && <InfoRow icon="document-text-outline" label="Message"><Text style={styles.infoRowValue}>{config.body}</Text></InfoRow>}
                                            </>
                                        )}
                                        {config.predefinedType === 'PERSONAL_PHONE_CALL' && config.callNotes && (
                                            <InfoRow icon="call-outline" label="Talking Points"><Text style={styles.infoRowValue}>{config.callNotes}</Text></InfoRow>
                                        )}
                                        <InfoRow icon="people-outline" label="Strategy" value={strategyLabel} />
                                    </>
                                );
                            }
                            const taskID = config.taskID || config.task?.id || null;
                            const task = pathsTasks.find(t => t.id === taskID) || config.task || null;
                            const taskName = task?.title || config.taskTitle || 'Task';
                            const members = (pathAssignmentMembers || []).filter(m => m.taskID === taskID).sort((a, b) => a.position - b.position);
                            let assignmentLabel = 'Assignment Pool';
                            let assignmentValue = `${members.length} member${members.length === 1 ? '' : 's'}`;
                            if (strategy === 'ASSIGN_ENROLLEE') {
                                assignmentLabel = 'Assignee';
                                assignmentValue = 'Assigns the enrollee';
                            } else if (strategy === 'ORDERED') {
                                assignmentLabel = 'Next Assignee';
                                const first = members[0];
                                assignmentValue = first
                                    ? `${first.Person?.firstName || ''} ${first.Person?.lastName || ''}`.trim() || 'Unknown'
                                    : 'Pool exhausted';
                            }
                            return (
                                <>
                                    <InfoRow icon="checkbox-outline" label="Task" value={taskName} />
                                    <InfoRow icon="people-outline" label="Strategy" value={strategyLabel} />
                                    <InfoRow icon="person-circle-outline" label={assignmentLabel} value={assignmentValue} />
                                </>
                            );
                        })()}
                        {selectedStep.stepType === 'CONDITION' && (selectedStep.config?.branches || []).map((b, i) => (
                            <InfoRow
                                key={b.id || i}
                                icon="git-branch-outline"
                                label={b.label || `Branch ${i + 1}`}
                                value={`${b.conditions?.length || 0} condition${b.conditions?.length === 1 ? '' : 's'}`}
                            />
                        ))}
                        {selectedStep.stepType === 'ACTION' && selectedStep.actionType === 'REQUEST_APPROVAL' && APPROVAL_BRANCHES.map(b => (
                            <InfoRow
                                key={b.id}
                                icon="git-branch-outline"
                                label={b.label}
                                value={`${selectedStep.config?.approverPersonIDs?.length || 0} approver${(selectedStep.config?.approverPersonIDs?.length || 0) === 1 ? '' : 's'}`}
                            />
                        ))}
                        <InfoRow icon="play-forward-outline" label="Advance" value={selectedStep.advanceOn === 'MANUAL' ? 'Manual' : 'Automatic'} />
                        <InfoRow
                            icon="power-outline"
                            label="Status"
                            value={selectedStep.isEnabled === false ? 'Disabled' : 'Enabled'}
                        />
                    </View>
                )}

                <SheetModal
                    isOpen={recurrenceModalOpen}
                    onCancel={() => setRecurrenceModalOpen(false)}
                    onClose={() => setRecurrenceModalOpen(false)}
                    title="Schedule"
                    cancelText="Close"
                    confirmText="Save"
                    onConfirm={handleSaveSchedule}
                >
                    <View style={{ gap: 12 }}>
                        <SheetSelector
                            type="roles"
                            title="Role for Runs"
                            placeholder="Select a role"
                            onChange={(role) => setSelectedRole(role || null)}
                            value={selectedRole}
                            testID="path-schedule-role"
                        />

                        <DatePickerInput
                            title="Start Date & Time"
                            mode="datetime"
                            value={recurrence.defaultStartTime}
                            onUpdate={(val) => setRecurrence((prev) => ({ ...prev, defaultStartTime: val }))}
                        />

                        <RecurrenceSettingsNew
                            key={recurrenceModalOpen ? 'open' : 'closed'}
                            isShowing={true}
                            eventDetails={recurrence}
                            setEventDetails={setRecurrence}
                        />
                    </View>
                </SheetModal>

                <SheetModal
                    isOpen={attendanceModalOpen}
                    onCancel={() => setAttendanceModalOpen(false)}
                    onClose={() => setAttendanceModalOpen(false)}
                    title="Attendance Trend Trigger"
                    cancelText="Close"
                    confirmText="Save"
                    onConfirm={handleSaveAttendance}
                >
                    <View style={{ gap: 12 }}>
                        <SelectInput
                            title="Mode"
                            value={attendanceMode}
                            options={[
                                { value: 'MISSED', label: 'Missed' },
                                { value: 'ATTENDED', label: 'Attended' },
                            ]}
                            onUpdate={(val) => setAttendanceMode(val)}
                            testID="path-attendance-mode"
                        />

                        <Input
                            title="Weeks"
                            placeholder="4"
                            type="numeric"
                            value={attendanceWeeks}
                            onUpdate={(val) => setAttendanceWeeks(val)}
                        />

                        <SheetSelector
                            type="roles"
                            title="Role (Optional)"
                            placeholder="Anyone in organization"
                            onChange={(role) => setAttendanceRole(role || null)}
                            value={attendanceRole}
                            testID="path-attendance-role"
                        />
                        {attendanceRole?.id && (
                            <TouchableOpacity
                                onPress={() => setAttendanceRole(null)}
                                style={{ alignSelf: 'flex-start' }}
                                hitSlop={8}
                                testID="path-attendance-role-clear"
                            >
                                <Text style={styles.attendanceClearRole}>Clear role</Text>
                            </TouchableOpacity>
                        )}

                        <Text style={styles.attendanceHelpText}>
                            {attendanceMode === 'MISSED'
                                ? 'Triggers when a person has attended before but hasn\u2019t checked in for the last N weeks. (Lapsed attendee)'
                                : 'Triggers when a person reaches N distinct weeks of attendance (all-time milestone).'}
                        </Text>
                        <Text style={styles.attendanceHelpText}>
                            {'Role (optional): Limit to members of a specific role. Leave empty to include anyone in the organization.'}
                        </Text>
                    </View>
                </SheetModal>
            </ScrollView>
        </View>
    );

    const renderScene = useCallback(({ route }) => {
        switch (route.key) {
            case 'flow':
                return isWeb ? (
                    <View style={styles.flowEditorContainer}>
                        <PathFlowEditor
                            path={path}
                            steps={steps}
                            edges={edges}
                            onEditStep={handleEditStep}
                            onAddStep={handleAddStep}
                            onUpdateTrigger={handleUpdateTrigger}
                            onConfigureTrigger={isV2 ? () => setTriggerSheetOpen(true) : undefined}
                            triggerOptions={isV2 ? (triggerCatalog || []).map(r => ({ value: r.triggerType, label: triggerLabel(r.triggerType) })) : undefined}
                            onSelectStep={setSelectedStep}
                            onEnrollPerson={canEnroll ? handleEnrollPerson : undefined}
                            onEnrollRole={canEnroll ? handleEnrollRole : undefined}
                            enrolling={enrolling}
                            onInsertStep={handleInsertStep}
                            onDuplicateStep={handleDuplicateStep}
                            triggeringForms={triggeringForms}
                            triggeringEvents={triggeringEvents}
                        />
                    </View>
                ) : (
                    <ScrollView
                        style={styles.timelineScroll}
                        contentContainerStyle={styles.timelineContent}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                    >
                        <PathFlowTimeline
                            path={path}
                            steps={steps}
                            edges={edges}
                            onEditStep={handleEditStep}
                            onAddStep={(loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) ? handleAddStep : undefined}
                            onUpdateTrigger={(loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) ? handleUpdateTrigger : undefined}
                            onConfigureTrigger={isV2 && (loggedInUser?.can?.("Update Paths") || loggedInUser?.isAdmin) ? () => setTriggerSheetOpen(true) : undefined}
                            triggerOptions={isV2 ? (triggerCatalog || []).map(r => ({ value: r.triggerType, label: triggerLabel(r.triggerType) })) : undefined}
                            onEnrollPerson={canEnroll ? handleEnrollPerson : undefined}
                            onEnrollRole={canEnroll ? handleEnrollRole : undefined}
                            enrolling={enrolling}
                            triggeringForms={triggeringForms}
                            triggeringEvents={triggeringEvents}
                        />
                    </ScrollView>
                );
            case 'enrollments':
                return (
                    <ScrollView
                        style={styles.enrollmentScroll}
                        contentContainerStyle={styles.enrollmentContent}
                        showsVerticalScrollIndicator={false}
                        keyboardShouldPersistTaps="handled"
                    >
                        {parkedReviewItems.length > 0 && (
                            <View style={styles.enrollmentSection} testID="parked-review-section">
                                <Text style={styles.enrollmentSectionTitle}>Needs Review ({parkedReviewItems.length})</Text>
                                {parkedReviewItems.map(item => (
                                    <TouchableOpacity
                                        key={item.enrollmentStepID}
                                        style={styles.reviewRow}
                                        activeOpacity={0.7}
                                        onPress={() => router.push({
                                            pathname: "/automations/enrollment/[enrollmentID]",
                                            params: { enrollmentID: item.enrollmentID, pathID },
                                        })}
                                        testID={`parked-review-${item.enrollmentStepID}`}
                                    >
                                        <View style={styles.reviewDot} />
                                        <View style={styles.reviewTextWrap}>
                                            <Text style={styles.reviewTitle} numberOfLines={1}>{item.title}</Text>
                                            <Text style={styles.reviewPerson} numberOfLines={1}>{item.personName}</Text>
                                        </View>
                                        <Unicon name="chevron-forward" size={16} colorVariant="textSecondary" />
                                    </TouchableOpacity>
                                ))}
                            </View>
                        )}
                        {activeEnrollments.length > 0 && (
                            <View style={styles.enrollmentSection}>
                                <Text style={styles.enrollmentSectionTitle}>Active ({activeEnrollments.length})</Text>
                                {activeEnrollments.map(e => (
                                    <EnrollmentCard
                                        key={e.id}
                                        enrollment={e}
                                        router={router}
                                        steps={steps}
                                        edges={edges}
                                        onPress={() => router.push({
                                            pathname: "/automations/enrollment/[enrollmentID]",
                                            params: { enrollmentID: e.id, pathID },
                                        })}
                                        onContinue={(step) => advanceStepMutation.mutate({ enrollmentStepID: step.id })}
                                        onSkipDelay={(step) => skipDelayMutation.mutate({ enrollmentStepID: step.id })}
                                    />
                                ))}
                            </View>
                        )}
                        {pastEnrollments.length > 0 && (
                            <View style={styles.enrollmentSection}>
                                <Text style={styles.enrollmentSectionTitle}>Past ({pastEnrollments.length})</Text>
                                {pastEnrollments.map(e => (
                                    <EnrollmentCard
                                        key={e.id}
                                        enrollment={e}
                                        router={router}
                                        steps={steps}
                                        edges={edges}
                                        onPress={() => router.push({
                                            pathname: "/automations/enrollment/[enrollmentID]",
                                            params: { enrollmentID: e.id, pathID },
                                        })}
                                    />
                                ))}
                            </View>
                        )}
                        {enrollments.length === 0 && (
                            <View style={styles.emptyEnrollments}>
                                <Unicon name="people-outline" size={40} colorVariant="textTertiary" />
                                <Text style={styles.emptyText}>No runs yet</Text>
                                <Text style={styles.emptySubtext}>People will appear here when this automation runs for them.</Text>
                                {canEnroll && (
                                    <View style={styles.emptyEnrollButtons}>
                                        <SheetSelector
                                            type="people"
                                            title="Run Person"
                                            placeholder="Select a person"
                                            value={null}
                                            onChange={handleEnrollPerson}
                                            disabled={enrolling}
                                        >
                                            <SolidButton
                                                variant="muted"
                                                type="primary"
                                                text="Start Run"
                                                iconName="person-add-outline"
                                                disabled={enrolling}
                                                testID="path-enroll-person-empty"
                                            />
                                        </SheetSelector>
                                    </View>
                                )}
                            </View>
                        )}
                    </ScrollView>
                );
            default: return null;
        }
    }, [path, steps, enrollments, isWeb, handleEditStep, handleAddStep, handleUpdateTrigger, activeEnrollments, pastEnrollments, loggedInUser, router, canEnroll, enrolling, handleEnrollPerson, handleEnrollRole, handleInsertStep, triggeringForms, triggeringEvents, isV2, triggerCatalog]);

    const renderTabBar = useCallback((props) => (
        <ReachTabBar {...props} scrollEnabled={false} />
    ), []);

    // ── Narrow: tabs include info ──
    const mobileRoutes = [{ key: 'info', title: 'Info' }, ...TAB_ROUTES];
    const renderMobileScene = useCallback(({ route }) => {
        if (route.key === 'info') return renderInfoPanel();
        return renderScene({ route });
    }, [renderScene, path, steps, enrollments, selectedStep]);


    if (isFetching && !path) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" />
            </View>
        );
    }

    if (!path) {
        return (
            <View style={styles.loadingContainer}>
                <Text style={styles.emptyText}>Automation not found</Text>
            </View>
        );
    }

      // ── Wide screen: 2-column ──
    const v2Sheets = isV2 ? (
        <>
            <StartRunSheet
                isOpen={startRunSheetOpen}
                onClose={() => setStartRunSheetOpen(false)}
                path={path}
                registry={v2Registry}
                catalogRow={triggerCatalogRow}
                onStarted={(result) => {
                    queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) });
                    if (result?.runID) {
                        router.push({
                            pathname: "/automations/enrollment/[enrollmentID]",
                            params: { enrollmentID: result.runID, pathID },
                        });
                    }
                }}
            />
            <TriggerConfigSheet
                isOpen={triggerSheetOpen}
                onClose={() => setTriggerSheetOpen(false)}
                catalog={triggerCatalog || []}
                initialType={triggerStep?.config?.triggerType}
                initialConfig={triggerStep?.config?.triggerConfig || {}}
                initialInputSchema={path?.inputSchema}
                organizationID={loggedInUser?.organizationID}
                saving={saveTriggerMutation.isPending}
                onSave={(t, cfg, inputSchema) => {
                    saveTriggerMutation.mutate({ triggerType: t, triggerConfig: cfg });
                    if (inputSchema !== undefined) {
                        // inputSchema lives on the Paths row — persist via publish payload fields.
                        supabase.from('Paths').update({ inputSchema }).eq('id', pathID).then(() =>
                            queryClient.invalidateQueries({ queryKey: QueryKeys.paths.byId(pathID) }));
                    }
                }}
            />
            <PublishResultSheet
                isOpen={!!publishSheetState}
                onClose={() => setPublishSheetState(null)}
                result={publishSheetState?.result}
                errors={publishSheetState?.errors || []}
                warnings={publishSheetState?.warnings || []}
                title={publishSheetState?.title}
                steps={steps}
                onReviewStep={(stepID) => {
                    const step = steps.find(s => s.id === stepID);
                    setPublishSheetState(null);
                    if (step) handleEditStep(step);
                }}
                onReload={publishSheetState?.showReload ? () => { setPublishSheetState(null); refresh(); } : undefined}
            />
        </>
    ) : null;

    if (isWideScreen) {
        return (
            <View style={styles.container}>
                <View style={styles.twoColumn}>
                    {renderInfoPanel()}
                    <View style={styles.rightPanel}>
                        <TabView
                            navigationState={{ index: tabIndex, routes: TAB_ROUTES }}
                            renderScene={renderScene}
                            onIndexChange={setTabIndex}
                            renderTabBar={renderTabBar}
                            swipeEnabled={isNative}
                            lazy
                        />
                    </View>
                </View>
                {v2Sheets}
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <TabView
                navigationState={{ index: tabIndex, routes: mobileRoutes }}
                renderScene={renderMobileScene}
                onIndexChange={setTabIndex}
                renderTabBar={renderTabBar}
                swipeEnabled
                lazy
            />
            {v2Sheets}
        </View>
    );
}

const styles = StyleSheet.create((theme) => ({
    container: { flex: 1, backgroundColor: 'transparent' },
    loadingContainer: { flex: 1, alignItems: 'center', justifyContent: 'center' },

    // 2-column
    twoColumn: { flex: 1, flexDirection: 'row' },
    leftPanel: { flex: 1, maxWidth: 420 },
    leftPanelContent: { padding: 16, paddingBottom: 100 },
    rightPanel: { flex: 2, flexGrow: 1 },

    // Header card
    headerCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        boxShadow: theme.shadows.card,
    },
    headerTitleRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginBottom: 16,
    },
    headerIconBadge: {
        width: 52,
        height: 52,
        borderRadius: 10,
        backgroundColor: theme.colors.pathsContainer,
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerTitleInfo: { flex: 1, marginLeft: 14 },
    headerTitle: {
        fontSize: 18,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    headerSubtitle: {
        fontSize: 13,
        color: theme.colors.textSecondary,
        lineHeight: 18,
        marginTop: 2,
    },
    statusRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginBottom: 16,
    },
    statusChip: (colorVariant) => ({
        flexDirection: 'row',
        alignItems: 'center',
        gap: 5,
        backgroundColor: theme.colors[colorVariant + 'Container'] || theme.colors.surfaceVariant,
        paddingHorizontal: 10,
        paddingVertical: 4,
        borderRadius: 20,
    }),
    statusDot: (colorVariant) => ({
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: theme.colors[colorVariant] || theme.colors.textSecondary,
    }),
    statusChipText: (colorVariant) => ({
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.textSecondary,
    }),

    // Stats row
    statsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 14,
        borderTopWidth: 1,
        borderBottomWidth: 1,
        borderColor: theme.colors.border,
        marginBottom: 16,
    },
    statItem: { flex: 1, alignItems: 'center' },
    statDivider: { width: 1, height: 28, backgroundColor: theme.colors.border },
    statLabel: { fontSize: 10, color: theme.colors.textSecondary, textTransform: 'uppercase', marginBottom: 2 },
    statValue: { fontSize: 13, fontFamily: theme.fonts.bold, color: theme.colors.text },

    // Actions
    actionRow: {
        flexDirection: 'row',
        gap: 8,
        flexWrap: 'wrap',
        marginTop: 4,
    },
    enrollButtonRow: {
        flexDirection: 'row',
        gap: 8,
        width: '100%',
        marginBottom: 0,
    },
    enrollButtonCell: {
        flex: 1,
    },

    attendanceHelpText: {
        fontSize: 12,
        fontFamily: theme.fonts.body,
        color: theme.colors.textSecondary,
        lineHeight: 18,
    },
    attendanceClearRole: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },

    // Info sections
    infoSection: {
        backgroundColor: theme.colors.surface,
        boxShadow: theme.shadows.card,
        borderRadius: 12,
        padding: 16,
        marginBottom: 16,
    },
    sectionLabel: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        marginBottom: 12,
    },
    stepDetailsHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 12,
    },
    editStepBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingVertical: 4,
        paddingHorizontal: 10,
        borderRadius: 8,
        backgroundColor: theme.colors.primaryContainer,
    },
    editStepBtnText: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.primary,
    },
    infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingVertical: 14,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    infoRowLeft: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        flex: 1,
        maxWidth: 140,
    },
    infoRowRight: {
        flex: 2,
        alignItems: 'flex-end',
    },
    infoRowLabel: {
        fontSize: 13,
        color: theme.colors.textSecondary,
    },
    infoRowValue: {
        fontSize: 14,
        color: theme.colors.text,
        textAlign: 'right',
    },
    linkText: {
        color: theme.colors.paths,
    },
    infoEmptyText: {
        fontStyle: 'italic',
        color: theme.colors.textTertiary,
    },
    conditionPill: {
        backgroundColor: theme.colors.surfaceVariant,
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 4,
        marginBottom: 3,
    },
    conditionPillText: { fontSize: 11, color: theme.colors.textSecondary },

    // Flow editor (web)
    flowEditorContainer: {
        flex: 1,
        _web: { minHeight: 400 },
    },

    // Timeline (native)
    timelineScroll: { flex: 1 },
    timelineContent: {
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 100,
        maxWidth: 700,
        alignSelf: 'center',
        width: '100%',
    },

    // Enrollments
    enrollmentScroll: { flex: 1 },
    enrollmentContent: {
        paddingHorizontal: 16,
        paddingTop: 16,
        paddingBottom: 100,
        maxWidth: 700,
        alignSelf: 'center',
        width: '100%',
    },
    enrollmentSection: { marginBottom: 24 },
    enrollmentSectionTitle: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.textSecondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
        marginBottom: 8,
    },
    enrollmentCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: 10,
        boxShadow: theme.shadows.card,
        padding: 12,
        marginBottom: 6,
        gap: 10,
        _web: {
            transition: 'all 0.15s ease-in-out',
            _hover: { backgroundColor: theme.colors.hover },
        },
    },
    enrollmentInfo: { flex: 1 },
    enrollmentName: {
        fontSize: 14,
        fontFamily: theme.fonts.heading,
        color: theme.colors.text,
    },
    enrollmentCurrentStep: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        marginTop: 1,
    },
    enrollmentFailedStep: {
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors.error,
        marginTop: 1,
    },
    progressBarContainer: {
        height: 4,
        backgroundColor: theme.colors.border,
        borderRadius: 2,
        marginTop: 6,
        overflow: 'hidden',
    },
    progressBarFill: (percent) => ({
        height: '100%',
        width: `${percent}%`,
        backgroundColor: theme.colors.success,
        borderRadius: 2,
    }),
    enrollmentMeta: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        marginTop: 3,
    },
    statusBadge: (colorVariant) => ({
        backgroundColor: theme.colors[colorVariant + 'Container'] || theme.colors.surfaceVariant,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6,
    }),
    statusBadgeText: (colorVariant) => ({
        fontSize: 11,
        fontFamily: theme.fonts.bold,
        color: theme.colors[colorVariant] || theme.colors.textSecondary,
    }),
    continueBtn: {
        backgroundColor: theme.colors.primary,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        marginLeft: 8,
    },
    continueBtnText: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: '#fff',
    },
    skipDelayBtn: {
        backgroundColor: theme.colors.warningContainer,
        paddingHorizontal: 12,
        paddingVertical: 6,
        borderRadius: 8,
        marginLeft: 8,
    },
    skipDelayBtnText: {
        fontSize: 12,
        fontFamily: theme.fonts.bold,
        color: theme.colors.warning,
    },

    // Parked review rows (v2 publish conflicts)
    reviewRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        backgroundColor: theme.colors.surface,
        borderRadius: 10,
        boxShadow: theme.shadows.card,
        padding: 12,
        marginBottom: 6,
    },
    reviewDot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: theme.colors.warning,
    },
    reviewTextWrap: { flex: 1 },
    reviewTitle: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    reviewPerson: {
        fontSize: 11,
        color: theme.colors.textTertiary,
        marginTop: 1,
    },

    // Empty states
    emptyEnrollments: {
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 60,
        gap: 8,
    },
    emptyText: {
        fontSize: 15,
        fontFamily: theme.fonts.heading,
        color: theme.colors.textSecondary,
    },
    emptySubtext: {
        fontSize: 13,
        color: theme.colors.textTertiary,
        textAlign: 'center',
    },
    emptyEnrollButtons: {
        marginTop: 12,
    },
}));
